import React, { useCallback, useEffect, useRef, useState } from 'react';
import { runStartup, snoozeRecommendation } from '../startup';
import { markHealthy } from '../bundles';
import type { ShellConfig } from '../checkIn';
import type { CapacitorShell } from '../capacitor/createCapacitorShell';
import { UpdateRequiredScreen, UpdateAvailableSheet } from '../ui/screens';
import { RubyGem } from '../ui/RubyGem';
import { PALETTE } from '../ui/tokens';
import type { ShellTheme } from '../ui/tokens';

type Phase = 'checking' | 'blocked' | 'app';

/** The gate never holds the app longer than this; past it, the app opens (spec §13: never block on our own outage). */
export const STARTUP_TIMEOUT_MS = 5000;
const TIMED_OUT = Symbol('timeout');

/**
 * The loading gem (RubyGem `drawing`) cuts its edges over a 2.4 s cycle and is
 * COMPLETE from ~1.55 s to ~1.92 s of each cycle, then erases. The app only
 * opens on a complete gem: at least one full draw, and if loading runs long it
 * draws again and opens at the end of that draw — never mid-stroke.
 */
export const GEM_CYCLE_MS = 2400;
export const GEM_DRAWN_AT_MS = 1600;
export const msUntilGemDrawn = (elapsedMs: number): number => {
  const n = Math.max(0, Math.ceil((elapsedMs - GEM_DRAWN_AT_MS) / GEM_CYCLE_MS));
  return n * GEM_CYCLE_MS + GEM_DRAWN_AT_MS - elapsedMs;
};
const reducedMotion = (): boolean => {
  try { return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true; } catch { return false; }
};

/**
 * Wraps the whole app. On the web (shell = null) it is a pass-through. On a
 * phone it runs the update gate before the first app screen and on every
 * return to the foreground (spec §4.2).
 */
export function MobileShellGate({ shell, config, fallbackStoreUrl, theme, children, startupTimeoutMs = STARTUP_TIMEOUT_MS, waitForGem = true }: {
  shell: CapacitorShell | null; config: ShellConfig | null; fallbackStoreUrl: string; theme: ShellTheme; children: React.ReactNode;
  startupTimeoutMs?: number;
  /** Open the app only on a fully drawn gem (see msUntilGemDrawn). Tests turn it off. */
  waitForGem?: boolean;
}) {
  const native = !!(shell && config);
  const [phase, setPhase] = useState<Phase>(native ? 'checking' : 'app');
  const [storeUrl, setStoreUrl] = useState<string>(fallbackStoreUrl);
  const [recommend, setRecommend] = useState(false);
  const healthy = useRef(false);
  const gemShownAt = useRef<number>(Date.now());

  // Leave the loading gem for the app — on a complete draw, never mid-stroke.
  const openApp = useCallback(() => {
    const wait = waitForGem && !reducedMotion() ? msUntilGemDrawn(Date.now() - gemShownAt.current) : 0;
    const go = () => setPhase((p) => (p === 'checking' ? 'app' : p));
    if (wait <= 0) go(); else setTimeout(go, wait);
  }, [waitForGem]);

  const run = useCallback(async (when: 'launch' | 'resume') => {
    if (!shell || !config) return;
    let o: Awaited<ReturnType<typeof runStartup>> | typeof TIMED_OUT;
    try {
      o = await Promise.race([
        runStartup(shell.ports, config, when),
        new Promise<typeof TIMED_OUT>((r) => setTimeout(() => r(TIMED_OUT), startupTimeoutMs)),
      ]);
    } catch {
      o = TIMED_OUT; // never leave the phone on the loading gem
    }
    if (o === TIMED_OUT) {
      openApp();
      return;
    }
    if (o.decision.screen === 'blocked') {
      setStoreUrl(o.decision.storeUrl || fallbackStoreUrl);
      setPhase('blocked');
      // The bundle itself is fine — stop the updater from reverting it.
      try { void shell.ports.updater.notifyReady().catch(() => {}); } catch { /* plugin unavailable */ }
      return;
    }
    setStoreUrl(o.checkIn.response.storeUrl || fallbackStoreUrl);
    setRecommend(o.decision.recommendUpdate);
    if (when === 'launch') openApp(); else setPhase('app');
  }, [shell, config, fallbackStoreUrl, startupTimeoutMs, openApp]);

  useEffect(() => {
    if (!native) return;
    void run('launch');
    return shell!.onResume(() => { void run('resume'); });
  }, [native, run, shell]);

  // The bundle is healthy once the app has actually rendered (Capgo keeps it).
  useEffect(() => {
    if (phase !== 'app' || !shell || healthy.current) return;
    healthy.current = true;
    try { void markHealthy(shell.ports.store, shell.ports.updater).catch(() => {}); } catch { /* plugin unavailable — nothing to confirm */ }
  }, [phase, shell]);

  if (!native) return <>{children}</>;
  if (phase === 'checking') {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: PALETTE[theme].ground }}>
        <RubyGem size={88} drawing />
      </div>
    );
  }
  if (phase === 'blocked') {
    return <UpdateRequiredScreen platform={shell!.platform} onOpenStore={() => { void shell!.openUrl(storeUrl); }} />;
  }
  return (
    <>
      {children}
      {recommend && (
        <UpdateAvailableSheet
          theme={theme}
          onUpdate={() => { void shell!.openUrl(storeUrl); }}
          onLater={() => { setRecommend(false); void snoozeRecommendation(shell!.ports.store, shell!.ports.now()); }}
        />
      )}
    </>
  );
}
